import { Button } from '@/components/ui/Button';
import {
  sendAlertReviewerTestNotification,
  TestNotificationChannel,
} from '@/features/Customers/alertReviewerTestNotification';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Mail, Send } from 'lucide-react';
import { toast } from 'sonner';

type TestNotificationButtonsProps = {
  customerId: string;
  // senhub tests the SAVED config, so the buttons are off while editing.
  editing: boolean;
  slackConfigured: boolean;
  emailConfigured: boolean;
};

const TestNotificationButtons = ({
  customerId,
  editing,
  slackConfigured,
  emailConfigured,
}: TestNotificationButtonsProps) => {
  const { mutate, isPending, variables } = useMutation({
    mutationKey: ['SendAlertReviewerTestNotification', customerId],
    mutationFn: (channel: TestNotificationChannel) =>
      sendAlertReviewerTestNotification(customerId, channel),
    onSuccess: (message) => {
      toast.success(message, { duration: 4000 });
    },
    onError: (error) => {
      toast.error('Test notification failed', {
        duration: 8000,
        description: error.message,
      });
    },
  });

  const renderButton = (
    channel: TestNotificationChannel,
    label: string,
    icon: React.ReactNode,
    configured: boolean,
    notConfiguredHint: string,
  ) => {
    const sending = isPending && variables === channel;
    const title = editing
      ? 'Save first - the test uses the saved settings'
      : configured
        ? undefined
        : notConfiguredHint;

    return (
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={editing || !configured || isPending}
        title={title}
        onClick={() => mutate(channel)}
      >
        {sending ? <Loader2 className="animate-spin" /> : icon}
        {label}
      </Button>
    );
  };

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap gap-2">
        {renderButton(
          'Slack',
          'Send test Slack notification',
          <Send />,
          slackConfigured,
          'Set and save a Slack webhook URL first',
        )}
        {renderButton(
          'Email',
          'Send test email',
          <Mail />,
          emailConfigured,
          'Set and save email recipients and an SMTP login first',
        )}
      </div>
      {editing && (
        <p className="text-muted-foreground text-sm">
          Save first - tests use the saved settings.
        </p>
      )}
    </div>
  );
};

export default TestNotificationButtons;
